import { Box, Button, ButtonGroup, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import gunmetalRivet from '../../../assets/hardware/rivets/gunmetal.png';
import { ReactComponent as Logo } from '../../../assets/svgs/logo.svg';
import PreviewButtons from '../buttons/PreviewButtons';

function ViaggioPreview({ leatherColors, threadColors, resetColors, getEffectiveColor, stitchColor, engraving, generateShareUrl }) {
    const [currentSide, setCurrentSide] = useState('front'); // 'front' or 'back'
    const [filename, setFilename] = useState('');
    const shareDescription = 'Check out my custom Viaggio Wallet design!';

    useEffect(() => {
        const baseColor = leatherColors?.find(c => c.id === getEffectiveColor('base'))?.name;
        const accentColor = leatherColors?.find(c => c.id === getEffectiveColor('accent'))?.name;
        const threadColor = threadColors?.find(c => c.hex === stitchColor)?.name;

        const newFilename = `${baseColor}-${accentColor}-${threadColor}-${currentSide}`;
        setFilename(newFilename);
    }, [leatherColors, threadColors, getEffectiveColor, stitchColor, currentSide]);

    const svgRef = useRef(null);

    const toggleView = (side) => {
        setCurrentSide(side);
    };

    return (
        <Stack
            sx={{
                width: { lg: '70%', md: '85%', sm: '100%' },
                margin: 'auto',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                direction: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3
            }}
        >
            <ButtonGroup variant="contained" sx={{ mt: 1 }}>
                <Button
                    onClick={() => toggleView('front')}
                    data-testid="front-button"
                    color={currentSide === 'front' ? 'primary' : 'default'}
                >
                    Front
                </Button>
                <Button
                    onClick={() => toggleView('back')}
                    data-testid="back-button"
                    color={currentSide === 'back' ? 'primary' : 'default'}
                >
                    Back
                </Button>
            </ButtonGroup>

            <Box width="100%" height="auto" sx={{ mt: 2 }}>
                <svg viewBox="0 0 75.785 100.4" ref={svgRef} style={{ stroke: '#000', strokeWidth: 0.25 }}>

                    <defs>
                        <clipPath id="wallet-front-base">
                            <path d="M333.391-23.234V68.466h72.797v-.16a6.456 6.456 0 0 1-.094-1.119v-.213c.06-1.71.942-2.95 2.357-3.887V7.574c-4.189-1.638-7.901-3.115-9.52-3.97l-60.89-25.412a25.065 25.065 0 0 0-4.65-1.426zm30.291 34.94 12.187.032a5.308 5.308 0 0 1 5.241 6.06l-3.166 22.14a7.022 7.022 0 0 1-6.968 6.028l-2.453-.007a7.047 7.047 0 0 1-6.959-6.06l-3.132-22.163a5.29 5.29 0 0 1 5.25-6.03z" transform="translate(-332.862 31.665)" />
                        </clipPath>
                        <clipPath id="wallet-front-rect">
                            <path style={{ width: .417655 }} transform="translate(-69.4 -152.929) scale(-1 1)" d="M-142.811 153.137h72.8v99.934h-72.8z" />
                        </clipPath>
                        <clipPath id="wallet-front-accent">
                            <path d="M260.793 174.772v81.616c0 2.762 2.26 4.837 4.896 4.028a24.24 24.24 0 0 0 2.372-.866l52.23-22.215c3.62-1.54 9.197-4.44 11.357-7.698 1.772-2.67 1.753-6.733 1.618-10.909V174.771a5 5 0 0 0-5-5h-62.473a5 5 135 0 0-5 5z" transform="rotate(-179.998 167.032 134.99)" />
                        </clipPath>
                        <clipPath id="wallet-back-base">
                            <path d="M70.012 191.313c-.751.297-1.38.555-2.177.867v55.513c1.08.716 1.847 1.61 2.177 2.755z" transform="translate(-67.638 -152.929)" />
                            <path style={{ width: .417655 }} transform="translate(-67.638 -152.929) scale(-1 1)" d="M-142.811 153.137h72.8v99.934h-72.8z" />
                        </clipPath>
                        <clipPath id="wallet-back-flap">
                            <path d="M67.835 192.458v55.152c3.444-2.065 9.647-2.557 15.728-3.119 2.216-.205 9.715-.424 11.733-.742 2.732-.43 4.865-3.39 4.85-6.16l-.116-23.135c-.016-3.192-1.469-8.005-4.085-9.692-5.74-3.704-18.653-8.61-28.11-12.304zm25.338 19.252a1.003 1.003 0 0 1 1.003 1.003 1.003 1.003 0 0 1-1.004 1.003 1.003 1.003 0 0 1-1.003-1.003 1.003 1.003 0 0 1 1.004-1.003zm0 24.419a1.003 1.003 0 0 1 1.003 1.003 1.003 1.003 0 0 1-1.004 1.003 1.003 1.003 0 0 1-1.003-1.003 1.003 1.003 0 0 1 1.004-1.003z" transform="translate(-67.638 -152.929)" />
                        </clipPath>
                    </defs>

                    {currentSide === 'front' && (
                        <g style={{ fill: 'none' }} >
                            <path d="M333.391-23.234V68.466h72.797v-.16a6.456 6.456 0 0 1-.094-1.119v-.213c.06-1.71.942-2.95 2.357-3.887V7.574c-4.189-1.638-7.901-3.115-9.52-3.97l-60.89-25.412a25.065 25.065 0 0 0-4.65-1.426zm30.291 34.94 12.187.032a5.308 5.308 0 0 1 5.241 6.06l-3.166 22.14a7.022 7.022 0 0 1-6.968 6.028l-2.453-.007a7.047 7.047 0 0 1-6.959-6.06l-3.132-22.163a5.29 5.29 0 0 1 5.25-6.03z" transform="translate(-332.862 31.665)" />
                            <path style={{ strokeLinecap: 'round', strokeLinejoin: 'round', strokeDasharray: 'none', strokeDashoffset: 0 }} d="M333.473-31.469h72.8v88.034h-72.8z" transform="translate(-332.862 31.665)" />
                            <g clipPath="url(#wallet-front-base)">
                                <image
                                    data-testid={`front-base-${getEffectiveColor('base')}`}
                                    href={leatherColors?.find(c => c.id === getEffectiveColor(`base`))?.texture}
                                    x="0"
                                    y="0"
                                    width="100%"
                                    height="100%"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                            </g>
                            <g clipPath="url(#wallet-front-rect)">
                                <image
                                    href={leatherColors?.find(c => c.id === getEffectiveColor(`base`))?.texture}
                                    x="0"
                                    y="0"
                                    width="100%"
                                    height="100%"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                            </g>
                            <g clipPath="url(#wallet-front-accent)">
                                <image
                                    data-testid={`front-accent-${getEffectiveColor('accent')}`}
                                    href={leatherColors?.find(c => c.id === getEffectiveColor(`accent`))?.texture}
                                    x="0"
                                    y="0"
                                    width="100%"
                                    height="100%"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                            </g>
                            <path d="M260.793 174.772v81.616c0 2.762 2.26 4.837 4.896 4.028a24.24 24.24 0 0 0 2.372-.866l52.23-22.215c3.62-1.54 9.197-4.44 11.357-7.698 1.772-2.67 1.753-6.733 1.618-10.909V174.771a5 5 0 0 0-5-5h-62.473a5 5 135 0 0-5 5z" transform="rotate(-179.998 167.032 134.99)" />
                            <path id="stitchline" data-testid="stitch-line" style={{ stroke: stitchColor, strokeWidth: '.8', strokeDasharray: '2,2', strokeDashoffset: 0 }} d="M260.264 256.388h4.539v-81.617c0-.594.396-.99.99-.99h62.472c.595 0 .99.396.99.99l-.044 49.551h4.857" transform="rotate(-179.998 167.032 134.99)" />

                            {engraving && (
                                <g stroke="#000" strokeWidth={0.25} fill="#000" opacity={0.8}>
                                    <text fontFamily="EB Garamond" fontSize={15} x={37} y={80} textAnchor="middle">
                                        {engraving}
                                    </text>
                                </g>
                            )}
                        </g>
                    )}

                    {currentSide === 'back' && (
                        <g>
                            <g clipPath="url(#wallet-back-base)">
                                <image
                                    data-testid={`back-base-${getEffectiveColor('base')}`}
                                    href={leatherColors?.find(c => c.id === getEffectiveColor(`base`))?.texture}
                                    x="0"
                                    y="0"
                                    width="100%"
                                    height="100%"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                            </g>

                            <g transform="translate(-67.638 -152.929)">
                                <path style={{ fill: 'none' }} d="M70.012 191.313c-.751.297-1.38.555-2.177.867v55.513c1.08.716 1.847 1.61 2.177 2.755z" />
                                <path style={{ fill: 'none', width: .417655 }} transform="scale(-1 1)" d="M-142.811 153.137h72.8v99.934h-72.8z" />
                            </g>

                            <g clipPath="url(#wallet-back-flap)">
                                <image
                                    href={leatherColors?.find(c => c.id === getEffectiveColor(`base`))?.texture}
                                    x="0"
                                    y="0"
                                    width="100%"
                                    height="100%"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                            </g>

                            <g transform="translate(-67.638 -152.929)">
                                <path style={{ fill: 'none' }} d="M67.835 192.458v55.152c3.444-2.065 9.647-2.557 15.728-3.119 2.216-.205 9.715-.424 11.733-.742 2.732-.43 4.865-3.39 4.85-6.16l-.116-23.135c-.016-3.192-1.469-8.005-4.085-9.692-5.74-3.704-18.653-8.61-28.11-12.304zm25.338 19.252a1.003 1.003 0 0 1 1.003 1.003 1.003 1.003 0 0 1-1.004 1.003 1.003 1.003 0 0 1-1.003-1.003 1.003 1.003 0 0 1 1.004-1.003zm0 24.419a1.003 1.003 0 0 1 1.003 1.003 1.003 1.003 0 0 1-1.004 1.003 1.003 1.003 0 0 1-1.003-1.003 1.003 1.003 0 0 1 1.004-1.003z" />
                                <image width="18.923" height="18.923" preserveAspectRatio="none" href={gunmetalRivet} x="83.422" y="202.217" />
                                <image width="18.923" height="18.923" preserveAspectRatio="none" href={gunmetalRivet} x="83.422" y="227.307" />
                            </g>

                            <g fill="#000" strokeWidth={0} opacity={0.75} transform='translate(30 15)' >
                                <Logo height="20px" width="18px" />
                            </g>
                        </g>
                    )}
                </svg>
            </Box>

            <PreviewButtons
                resetColors={resetColors}
                shareDescription={shareDescription}
                generateShareUrl={generateShareUrl}
                svgRef={svgRef}
                filename={filename}
            />
        </Stack>
    );
}

ViaggioPreview.propTypes = {
    leatherColors: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        swatch: PropTypes.string.isRequired,
        texture: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
    })).isRequired,
    threadColors: PropTypes.arrayOf(PropTypes.shape({
        hex: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
    })).isRequired,
    getEffectiveColor: PropTypes.func.isRequired,
    resetColors: PropTypes.func.isRequired,
    stitchColor: PropTypes.string.isRequired,
    engraving: PropTypes.string,
    generateShareUrl: PropTypes.func.isRequired
};

export default ViaggioPreview;
